import { type FormEvent, useEffect, useId, useRef, useState } from 'react';

import { errorMessage } from '@/shared/api/errors';
import { Button } from '@/shared/ui/Button';
import { Callout } from '@/shared/ui/Callout';
import { ErrorMessage } from '@/shared/ui/ErrorMessage';
import { CopyIcon, DownloadIcon } from '@/shared/ui/icons';
import { Input } from '@/shared/ui/Input';
import { PasswordInput } from '@/shared/ui/PasswordInput';
import { Spinner } from '@/shared/ui/Spinner';

import {
  CODE_DIGITS,
  type CodeKind,
  codeErrorMessage,
  isWrongPassword,
  needsRestart,
  onlyDigits,
  recoveryCodesFile,
  restartMessage,
} from './twoFactorText';
import type { TwoFactorEnabled, User } from './types';
import { useConfirmTwoFactor, useTwoFactorSetup, useVerifySecondFactor } from './useTwoFactor';

/**
 * The pieces of the second factor (contract v10.45, ADR 0039 of the back), shared by the
 * door (`LoginPage`, after the password) and the profile (`ProfilePage`, to turn it on or
 * to ask for new recovery codes). Each one is a block without its own title: the screen
 * around it says where the person is.
 */

/**
 * The code being typed. The app's is six digits, with the keypad of numbers on a phone and
 * `one-time-code` so the browser can offer it; anything that is not a digit is dropped as
 * it is typed («123 456» is what people copy from the app). A recovery code is letters,
 * as it was printed (`xxxxx-xxxxx`). It takes the focus when it appears.
 */
export function CodeField({
  kind,
  value,
  onChange,
  error,
}: {
  kind: CodeKind;
  value: string;
  onChange: (value: string) => void;
  error?: string | undefined;
}) {
  if (kind === 'recovery') {
    return (
      <Input
        label="Código de recuperación"
        name="recovery-code"
        autoComplete="off"
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        placeholder="xxxxx-xxxxx"
        maxLength={40}
        autoFocus
        className="font-mono"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        hint="Uno de los diez que guardaste al activarlo. Cada uno sirve una vez."
        error={error}
      />
    );
  }
  return (
    <Input
      label="Código de seis cifras"
      name="one-time-code"
      inputMode="numeric"
      autoComplete="one-time-code"
      pattern="[0-9]*"
      autoFocus
      className="font-mono text-[17px] tracking-[0.25em]"
      value={value}
      onChange={(event) => onChange(onlyDigits(event.target.value))}
      hint="Cambia cada 30 segundos: escribe el que sale ahora."
      error={error}
    />
  );
}

/** «Recordar este dispositivo»: the hint hangs off `aria-describedby`, not off the label. */
function RememberDevice({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  const id = useId();
  return (
    <div className="flex items-start gap-2">
      <input
        id={id}
        type="checkbox"
        className="accent-accent mt-0.5 size-4 shrink-0"
        checked={checked}
        aria-describedby={`${id}-hint`}
        onChange={(event) => onChange(event.target.checked)}
      />
      <div className="flex flex-col">
        <label htmlFor={id} className="text-ink-2 text-[14px]">
          Recordar este dispositivo 30 días
        </label>
        <p id={`${id}-hint`} className="text-ink-3 text-[12.5px] leading-snug">
          En este navegador no te pedirá el código durante 30 días. No lo marques en un ordenador
          que usa más gente.
        </p>
      </div>
    </div>
  );
}

/**
 * The code after the password, for whoever already has the app (`second_factor: verify`).
 * «Usar un código de recuperación» swaps the field for one of the ten codes, for a day
 * without the phone. A wrong code is said here; a pending step that ran out (ten minutes
 * after the password) sends the person back to the password with `onRestart`.
 */
export function VerifyPanel({
  onSignedIn,
  onRestart,
}: {
  onSignedIn: (user: User) => void;
  onRestart: (message: string) => void;
}) {
  const verify = useVerifySecondFactor();
  const [kind, setKind] = useState<CodeKind>('app');
  const [code, setCode] = useState('');
  const [remember, setRemember] = useState(false);
  const ready = kind === 'app' ? code.length === CODE_DIGITS : code.trim().length > 0;
  const error =
    verify.isError && !needsRestart(verify.error)
      ? codeErrorMessage(verify.error, kind)
      : undefined;

  function type(value: string) {
    setCode(value);
    if (verify.isError) verify.reset();
  }

  function swap() {
    setKind((current) => (current === 'app' ? 'recovery' : 'app'));
    setCode('');
    verify.reset();
  }

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!ready || verify.isPending) return;
    verify.mutate(
      kind === 'app'
        ? { code, remember_device: remember }
        : { recovery_code: code.trim(), remember_device: remember },
      {
        onSuccess: onSignedIn,
        onError: (failure) => {
          if (needsRestart(failure)) onRestart(restartMessage(failure));
        },
      },
    );
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
      {/* A key per kind: the field mounts again, so it takes the focus again. */}
      <CodeField key={kind} kind={kind} value={code} onChange={type} error={error} />
      <RememberDevice checked={remember} onChange={setRemember} />
      <Button type="submit" size="lg" disabled={!ready || verify.isPending}>
        {verify.isPending ? 'Comprobando…' : 'Entrar'}
      </Button>
      <button type="button" className="link self-start text-[14px]" onClick={swap}>
        {kind === 'app' ? 'Usar un código de recuperación' : 'Usar el código de la aplicación'}
      </button>
    </form>
  );
}

/**
 * Setting up the app: the QR, the secret for whoever cannot scan it (and, on a phone, the
 * link that opens the app directly, since nobody can scan their own screen), and the first
 * code, which turns it on. The QR is drawn by the back as an SVG `data:` URI: the secret
 * never leaves for anybody else to draw it.
 *
 * At the door (`allowRemember`, `onRestart`) it comes after the password of somebody who
 * has to set it up; in the profile, it is «Activar» for somebody already in, and the API
 * asks for the password again there (`askPassword`): a session left open on a shared
 * screen must not be enough to tie the account to somebody else's phone.
 */
export function SetupPanel({
  submitLabel,
  allowRemember = false,
  askPassword = false,
  onEnabled,
  onRestart,
  onCancel,
}: {
  submitLabel: string;
  allowRemember?: boolean;
  askPassword?: boolean;
  onEnabled: (result: TwoFactorEnabled) => void;
  onRestart?: (message: string) => void;
  onCancel?: () => void;
}) {
  const setup = useTwoFactorSetup();
  const confirm = useConfirmTwoFactor();
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  /** The server forgot the QR on screen (ten minutes went by with the profile open) and a
   * new one was asked for: whatever the app holds from the old one will never be right. */
  const [renewedQr, setRenewedQr] = useState(false);
  const ready = code.length === CODE_DIGITS && (!askPassword || password.length >= 8);

  // The QR could not be asked for because the ten minutes after the password ran out:
  // nothing on this screen fixes that, the password does.
  const restart = setup.isError && needsRestart(setup.error) && onRestart ? onRestart : null;
  const setupError = setup.error;
  useEffect(() => {
    if (restart) restart(restartMessage(setupError, 'setup'));
  }, [restart, setupError]);

  const expired = confirm.isError && confirm.error.status === 409;
  const failed = confirm.isError && !expired && !(onRestart && needsRestart(confirm.error));
  const passwordError =
    failed && isWrongPassword(confirm.error) ? errorMessage(confirm.error) : undefined;
  const error = failed && !passwordError ? codeErrorMessage(confirm.error, 'app') : undefined;

  function type(value: string) {
    setCode(value);
    if (confirm.isError) confirm.reset();
  }

  function typePassword(value: string) {
    setPassword(value);
    if (confirm.isError) confirm.reset();
  }

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!ready || confirm.isPending) return;
    confirm.mutate(
      {
        code,
        remember_device: allowRemember && remember,
        ...(askPassword ? { current_password: password } : {}),
      },
      {
        onSuccess: onEnabled,
        onError: (failure) => {
          if (onRestart && needsRestart(failure)) {
            onRestart(restartMessage(failure, 'setup'));
            return;
          }
          // 409: the QR of this screen is not the one the server waits for any more. A new
          // one, and the code typed from the old one is no use. (Already on, from another
          // tab, is a 409 too: then asking for a QR answers it, and that is what shows.)
          if (failure.status === 409) {
            setCode('');
            setRenewedQr(true);
            void setup.refetch();
          }
        },
      },
    );
  }

  if (setup.isPending || (renewedQr && setup.isFetching)) {
    return <Spinner variant="fill" label="Preparando el código QR" />;
  }

  if (setup.isError) {
    if (restart) return null;
    return (
      <div className="flex flex-col gap-3">
        <ErrorMessage error={setup.error}>No se pudo preparar el código QR</ErrorMessage>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="secondary"
            onClick={() => void setup.refetch()}
            disabled={setup.isFetching}
          >
            {setup.isFetching ? 'Probando…' : 'Volver a intentarlo'}
          </Button>
          {onCancel ? (
            <Button type="button" variant="ghost" onClick={onCancel}>
              Cancelar
            </Button>
          ) : null}
        </div>
      </div>
    );
  }

  const { qr, secret, otpauth_uri: link, issuer, account } = setup.data;

  return (
    <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
      {renewedQr ? (
        <Callout tone="warn" title="El código QR anterior ha caducado">
          Este es uno nuevo: escanéalo. Si ya habías escaneado el anterior, borra esa entrada de la
          aplicación, porque ya no sirve.
        </Callout>
      ) : null}
      <ol className="text-ink-2 flex list-decimal flex-col gap-1 pl-5 text-[14px] leading-snug">
        <li>
          Instala en el móvil una aplicación autenticadora, como Google Authenticator o Microsoft
          Authenticator.
        </li>
        <li>Ábrela, escanea este código y escribe abajo las seis cifras que te dé.</li>
      </ol>

      <div className="flex justify-center">
        {/* White under the QR whatever the paper: a reader needs the contrast. Its own
            size: the SVG has no viewBox, and a scaled one would not be the same drawing. */}
        <img
          src={qr}
          alt={`Código QR para añadir ${issuer} a la aplicación del móvil`}
          className="border-line max-w-full rounded-md border bg-white p-1"
        />
      </div>

      <div className="text-ink-3 flex flex-col gap-1 text-[13px] leading-snug">
        <p>
          ¿No puedes escanearlo? Escribe esta clave en la aplicación:{' '}
          <code className="text-ink font-mono text-[13.5px] break-all select-all">{secret}</code>
        </p>
        <p className="break-words">
          En la aplicación saldrá como «{issuer}» con tu cuenta, {account}.
        </p>
        <a href={link} className="link self-start sm:hidden">
          Si estás en el mismo móvil, ábrelo en la aplicación
        </a>
      </div>

      <CodeField kind="app" value={code} onChange={type} error={error} />
      {askPassword ? (
        <PasswordInput
          label="Tu contraseña"
          name="current-password"
          autoComplete="current-password"
          value={password}
          onChange={(event) => typePassword(event.target.value)}
          hint="Se pide otra vez para activarlo. Se cerrarán las demás sesiones abiertas con tu cuenta."
          error={passwordError}
          required
        />
      ) : null}
      {allowRemember ? <RememberDevice checked={remember} onChange={setRemember} /> : null}
      <div className="flex flex-wrap gap-2">
        <Button
          type="submit"
          size={allowRemember ? 'lg' : 'md'}
          className={allowRemember ? 'w-full' : undefined}
          disabled={!ready || confirm.isPending}
        >
          {confirm.isPending ? 'Comprobando…' : submitLabel}
        </Button>
        {onCancel ? (
          <Button type="button" variant="ghost" onClick={onCancel} disabled={confirm.isPending}>
            Cancelar
          </Button>
        ) : null}
      </div>
    </form>
  );
}

/** A text file through an object URL, which is same origin, so `download` is honoured. */
function saveText(text: string, filename: string): void {
  const url = URL.createObjectURL(new Blob([text], { type: 'text/plain;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.append(link);
  link.click();
  link.remove();
  // Revoked on the next tick: a URL revoked in the same tick as the click downloads nothing
  // in some browsers.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/**
 * The ten recovery codes, shown **once**: the server keeps only their hash. Copy and a
 * text file to print or keep; «Los he guardado» before the way on, because closing this
 * without them is closing the only door for a day without the phone.
 *
 * The list takes the focus when it appears (the form that produced it is gone), so a
 * screen reader starts on «Códigos de recuperación, lista, 10 elementos».
 */
export function RecoveryCodesPanel({
  codes,
  account,
  doneLabel,
  onDone,
}: {
  codes: string[];
  account: string;
  doneLabel: string;
  onDone: () => void;
}) {
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);
  const list = useRef<HTMLOListElement>(null);

  useEffect(() => {
    list.current?.focus();
  }, []);

  async function copy() {
    try {
      await navigator.clipboard.writeText(codes.join('\n'));
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <Callout tone="warn" role="status" title="Se enseñan esta vez y ninguna más">
        Cada código sirve una sola vez para entrar si no tienes el móvil a mano. Guárdalos en papel
        o en un gestor de contraseñas, no en este mismo móvil.
      </Callout>

      <ol
        ref={list}
        tabIndex={-1}
        aria-label="Códigos de recuperación"
        className="bg-paper-2 border-line grid grid-cols-2 gap-x-4 gap-y-1.5 rounded-md border px-4 py-3 font-mono text-[15px] outline-none"
      >
        {codes.map((code) => (
          <li key={code} className="text-ink select-all">
            {code}
          </li>
        ))}
      </ol>

      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="secondary"
          icon={<CopyIcon size={16} />}
          onClick={() => void copy()}
        >
          {copied ? 'Copiados' : 'Copiar'}
        </Button>
        <Button
          type="button"
          variant="secondary"
          icon={<DownloadIcon size={16} />}
          onClick={() =>
            saveText(recoveryCodesFile(codes, account, new Date()), 'codigos-de-recuperacion.txt')
          }
        >
          Descargar
        </Button>
      </div>

      <label className="text-ink-2 flex items-start gap-2 text-[14px]">
        <input
          type="checkbox"
          className="accent-accent mt-0.5 size-4 shrink-0"
          checked={saved}
          onChange={(event) => setSaved(event.target.checked)}
        />
        Los he guardado
      </label>
      <Button type="button" size="lg" disabled={!saved} onClick={onDone}>
        {doneLabel}
      </Button>
    </div>
  );
}
